import React from 'react';
import Icon from '../../../components/AppIcon';

const ValidationStatus = ({ validationResults, isVisible }) => {
  if (!isVisible || !validationResults || validationResults.length === 0) {
    return null;
  }

  const getStatusConfig = (type) => {
    switch (type) {
      case 'success':
        return {
          icon: 'CheckCircle',
          iconColor: 'var(--color-success)',
          bgColor: 'bg-success/10',
          borderColor: 'border-success/20',
          textColor: 'text-success'
        };
      case 'warning':
        return {
          icon: 'AlertTriangle',
          iconColor: 'var(--color-warning)',
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning/20',
          textColor: 'text-warning'
        };
      case 'error':
        return {
          icon: 'AlertCircle',
          iconColor: 'var(--color-error)',
          bgColor: 'bg-error/10',
          borderColor: 'border-error/20',
          textColor: 'text-error'
        };
      case 'info':
      default:
        return {
          icon: 'Info',
          iconColor: 'var(--color-secondary)',
          bgColor: 'bg-secondary/10',
          borderColor: 'border-secondary/20',
          textColor: 'text-secondary'
        };
    }
  };

  const groupedResults = validationResults.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {});

  return (
    <div className="w-full max-w-2xl mx-auto mt-6">
      <div className="space-y-4">
        {Object.entries(groupedResults).map(([type, results]) => {
          const config = getStatusConfig(type);
          
          return (
            <div
              key={type}
              className={`
                ${config.bgColor} ${config.borderColor} border rounded-lg p-4
                animate-slide-in
              `}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  <Icon 
                    name={config.icon} 
                    size={20} 
                    color={config.iconColor}
                    strokeWidth={2}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className={`font-body font-medium ${config.textColor} mb-2`}>
                    {type.charAt(0).toUpperCase() + type.slice(1)} ({results.length})
                  </div>

                  <div className="space-y-2">
                    {results.map((result, index) => (
                      <div key={index} className="text-sm text-muted-foreground font-body">
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-foreground">
                            {result.fileName}:
                          </span>
                          <span>{result.message}</span>
                        </div>
                        
                        {result.details && (
                          <div className="mt-1 ml-4 text-xs text-muted-foreground/80">
                            {result.details}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-4 p-3 bg-muted/50 rounded-lg">
        <div className="flex items-center justify-between text-sm font-body">
          <span className="text-muted-foreground">
            Validation Summary
          </span>
          
          <div className="flex items-center space-x-4">
            {groupedResults.success && (
              <div className="flex items-center space-x-1 text-success">
                <Icon name="CheckCircle" size={14} color="var(--color-success)" />
                <span>{groupedResults.success.length} passed</span>
              </div>
            )}
            
            {groupedResults.warning && (
              <div className="flex items-center space-x-1 text-warning">
                <Icon name="AlertTriangle" size={14} color="var(--color-warning)" />
                <span>{groupedResults.warning.length} warnings</span>
              </div>
            )}
            
            {groupedResults.error && (
              <div className="flex items-center space-x-1 text-error">
                <Icon name="AlertCircle" size={14} color="var(--color-error)" />
                <span>{groupedResults.error.length} errors</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidationStatus;